import { useMutation, useQueryClient } from "@tanstack/react-query";
import services from "../services";
import { ORDER } from "../enums";
import useNotification from "../../../utils/useNotification";
import { PartConfirmationBucketEntry } from "../../../components/SupplierInventorySearch/utils/partsCalculationUtils";

export type PartConfirmationSendStatus =
  | "sent"
  | "created_without_notify"
  | "failed";

export interface PartConfirmationSendResult {
  supplierId: string;
  supplierName: string;
  status: PartConfirmationSendStatus;
  error?: string;
}

interface SendPartConfirmationBucketInput {
  orderId: string;
  orderNo: string;
  entries: PartConfirmationBucketEntry[];
}

const extractErrorMessage = (error: unknown): string => {
  const anyError = error as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return (
    anyError?.response?.data?.message ||
    anyError?.message ||
    "Failed to send part confirmation"
  );
};

/**
 * Sends a part-confirmation request to every supplier in the Part Confirmation Bucket via
 * one `POST /v1/part-confirmations/batch`. The backend isolates each supplier, so one
 * failure never blocks the rest, and records the successful requests as one batch.
 */
export const useSendPartConfirmationBucket = () => {
  const queryClient = useQueryClient();
  const notify = useNotification();

  return useMutation({
    mutationFn: async ({
      orderId,
      orderNo,
      entries,
    }: SendPartConfirmationBucketInput): Promise<
      PartConfirmationSendResult[]
    > => {
      const { results } = await services.createPartConfirmationBatch({
        orderId,
        orderNo,
        entries: entries.map((entry) => ({
          supplierId: entry.supplierId,
          parts: entry.confirmationParts,
        })),
      });

      return entries.map((entry, index) => ({
        supplierId: entry.supplierId,
        supplierName: entry.supplierName,
        status: results[index]?.status ?? "failed",
        ...(results[index]?.error ? { error: results[index].error } : {}),
      }));
    },
    onSuccess: (results, variables) => {
      const sentCount = results.filter((r) => r.status === "sent").length;
      const createdWithoutNotifyCount = results.filter(
        (r) => r.status === "created_without_notify",
      ).length;
      const failedCount = results.filter((r) => r.status === "failed").length;
      const createdCount = sentCount + createdWithoutNotifyCount;

      queryClient.invalidateQueries({
        queryKey: [ORDER.PART_CONFIRMATION_RESPONSES],
      });
      queryClient.invalidateQueries({
        queryKey: [ORDER.PART_CONFIRMATIONS_FOR_ORDER],
      });
      queryClient.invalidateQueries({
        queryKey: [ORDER.SUPPLIER_INVENTORY_DELTAS],
      });

      if (failedCount === 0 && createdWithoutNotifyCount === 0) {
        notify({
          status: "success",
          title: "Part confirmation sent",
          description: `Sent to ${sentCount} supplier${
            sentCount === 1 ? "" : "s"
          } for enquiry ${variables.orderNo}`,
          duration: 4000,
        });
        return;
      }

      if (failedCount === 0 && createdWithoutNotifyCount > 0) {
        notify({
          status: "warning",
          title: "Part confirmation created",
          description:
            sentCount > 0
              ? `${sentCount} supplier${
                  sentCount === 1 ? " was" : "s were"
                } notified; ${createdWithoutNotifyCount} ${
                  createdWithoutNotifyCount === 1 ? "was" : "were"
                } not notified because business hours have ended for enquiry ${
                  variables.orderNo
                }`
              : `The supplier${
                  createdWithoutNotifyCount === 1 ? "" : "s"
                } ${
                  createdWithoutNotifyCount === 1
                    ? "was"
                    : `(${createdWithoutNotifyCount}) were`
                } not notified because business hours have ended for enquiry ${
                  variables.orderNo
                }`,
          duration: 6000,
        });
        return;
      }

      const failureDetails = results
        .filter((r) => r.status === "failed")
        .map((r) => `${r.supplierName}: ${r.error}`);
      const nonNotifiedDetails = results
        .filter((r) => r.status === "created_without_notify")
        .map(
          (r) =>
            `${r.supplierName}: not notified because business hours have ended`,
        );

      notify({
        status: createdCount > 0 ? "warning" : "error",
        title: `${createdCount} created, ${failedCount} failed`,
        description: [...failureDetails, ...nonNotifiedDetails].join(" | "),
        duration: 6000,
      });
    },
    onError: (error) => {
      notify({
        status: "error",
        title: "Failed to send part confirmation",
        description: extractErrorMessage(error),
      });
    },
  });
};
